import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useRouteMatch } from 'react-router';
import moment from 'moment';

import MediumButtonStyled from '../../components/Buttons/MediumButtonStyled';
import ContentBoardStyled from '../../components/ContentBoardStyled';
import { formattedDate, secondToTime } from '../../utils/date';
import NoItemsInList from '../../components/NoItemsInList';
import ContextMenu from '../../components/ContextMenu';
import { ControlBarContext } from '../ControlBarContext';
import Scrollable from '../../components/Scrollable';
import DateInput from './components/DateInput';
import useSummary from '../../api/useSummary';
import Loader from '../../components/Loader';
import { AuthContext } from '../../Auth';
import doFetch from '../../api/doFetch';
import {
  ActivityTitle,
  ActivityHeader,
  ActivityHeaderCol,
  ActivityHeaderInfoCol,
  ActivityTable,
  ActivityTableRow
} from './ui/styles';

const ActivityPage = ({ getBreadCrumbs }) => {
  const { setBreadCrumbs, setRightSide } = useContext(ControlBarContext);
  const [contextParent, setContextParent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const todayRange = moment.range(moment().startOf('day'), moment().startOf('day'));
    return todayRange;
  });
  const { summary, isLoading } = useSummary(
    formattedDate(selectedDate.start, 'y-MM-DD'),
    formattedDate(selectedDate.end || selectedDate.start, 'y-MM-DD')
  );
  const { currentUser } = useContext(AuthContext);
  const match = useRouteMatch();

  const canSeePrice = useMemo(() => {
    return ['admin'].includes(currentUser?.role);
    // eslint-disable-next-line
  }, [currentUser?.role]);

  const performExport = (type) => {
    doFetch(
      `/reports/users/summary/download/${type === 'csv' ? 'csv' : ''}?start=${formattedDate(
        selectedDate.start,
        'y-MM-DD'
      )}&end=${formattedDate(selectedDate.end || selectedDate.start, 'y-MM-DD')}`,
      { responseType: 'blob' }
    )
      .then((response) => response.blob())
      .then((response) => {
        const file = new Blob([response], { type: `application/${type}` });
        const fileURL = URL.createObjectURL(file);
        var a = document.createElement('a');
        a.href = fileURL;
        a.download = `Daily activity - ${formattedDate(selectedDate)}.${type}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch((error) => alert(error));
  };

  useEffect(
    () =>
      setRightSide({
        Component: (
          <>
            <MediumButtonStyled
              onClick={(e) => setContextParent(e.target)}
              disabled={!summary?.items?.length}
            >
              Download report
            </MediumButtonStyled>
          </>
        ),
        url: match.url
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setRightSide, selectedDate, match.url, summary?.items]
  );

  const periodDuration = useMemo(() => {
    if (!selectedDate?.start || !selectedDate?.end) return 1;

    return selectedDate.end.diff(selectedDate.start, 'days') + 1;
  }, [selectedDate]);

  const numberOfRows = useMemo(() => {
    let rows = periodDuration === 1 ? 10 : 8;
    if (canSeePrice) rows += 1;
    return rows;
  }, [canSeePrice, periodDuration]);

  useEffect(() => setBreadCrumbs(getBreadCrumbs()), [setBreadCrumbs, getBreadCrumbs]);

  return (
    <ContentBoardStyled style={{ padding: '18px 22px' }}>
      {Boolean(contextParent) && (
        <ContextMenu
          options={[
            {
              name: 'Export PDF report',
              action: () => performExport('pdf')
            },
            {
              name: 'Export CSV report',
              action: () => performExport('csv')
            }
          ]}
          parent={contextParent}
          onClose={() => setContextParent(null)}
        />
      )}
      <ActivityHeader>
        <ActivityHeaderCol>
          <ActivityTitle>Users statistics</ActivityTitle>
          <DateInput selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
        </ActivityHeaderCol>
        <ActivityHeaderInfoCol>
          <div>
            <div>
              <span>Total units</span>
              <span>{summary?.stats.units || '–'}</span>
            </div>
            <div>
              <span>Total Full shoot</span>
              <span>{summary?.stats.totalFullShoot || '–'}</span>
            </div>
            {canSeePrice && (
              <div>
                <span>Total amount, $</span>
                <span>{summary?.stats?.price || '–'}</span>
              </div>
            )}
          </div>
          <div>
            <div>
              <span>Total average time</span>
              <span>{summary?.stats.avg ? secondToTime(summary?.stats.avg) : '–'}</span>
            </div>
            <div>
              <span>Total Short shoot</span>
              <span>{summary?.stats?.totalShortShoot || '–'}</span>
            </div>
            {canSeePrice && (
              <div>
                <span></span>
                <span></span>
              </div>
            )}
          </div>
        </ActivityHeaderInfoCol>
      </ActivityHeader>

      {!!summary?.items?.length ? (
        <ActivityTable>
          <ActivityTableRow head columns={numberOfRows}>
            <div>Name</div>
            <div>Units</div>
            <div>Avg. units per day</div>
            <div>Dealerships</div>
            {periodDuration === 1 && (
              <>
                <div>First shoot</div>
                <div>Last shoot</div>
              </>
            )}
            <div>Avg. time per shoot</div>
            <div>Full shoot </div>
            <div>Short shoot</div>
            <div>Manager</div>
            {canSeePrice && <div>Total amount, $</div>}
          </ActivityTableRow>
          <Scrollable>
            {summary?.items.map((row, index) => (
              <ActivityTableRow key={index} columns={numberOfRows}>
                <div>{row.displayName}</div>
                <div>{row.units}</div>
                <div>{row.avgMount}</div>
                <div>{row.dealerships}</div>
                {periodDuration === 1 && (
                  <>
                    <div>{row.first}</div>
                    <div>{row.last}</div>
                  </>
                )}
                <div>{row.avgTime}</div>
                <div>{row.totalFullShoot}</div>
                <div>{row.totalShortShoot}</div>
                <div>{row.manager}</div>
                {canSeePrice && <div>{row.price}</div>}
              </ActivityTableRow>
            ))}
          </Scrollable>
        </ActivityTable>
      ) : (
        <NoItemsInList>{!isLoading ? 'No reports to show' : ''}</NoItemsInList>
      )}
      <Loader isLoading={isLoading} firstLoad={true} />
    </ContentBoardStyled>
  );
};

export default ActivityPage;
